import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconLogout, IconShieldLock } from "@tabler/icons-react";

import {
  adminFetch,
  clearAdminSession,
  getAdminSession,
} from "../lib/adminAuth";

/**
 * Platform Admin console · service provider (ASI Builder).
 * Tabs: Tenants (lifecycle + package + credits) · Packages · Admins.
 * All data via /platform-admin/* routes (PLATFORM_ADMIN JWT required).
 */

interface TenantRow {
  tenant: {
    id: string;
    code: string;
    name: string;
    clientType: string;
    tier: string;
    status: string;
    subscriptionPackageId: string | null;
    aiGradingEnabled: boolean;
    aiGradingCreditsBalance: number;
    aiGradingCreditsMonthly: number;
    aiForbidden: boolean;
  };
  package: { id: string; code: string; name: string } | null;
}
interface Pkg {
  id: string;
  code: string;
  name: string;
  maxCompanies: number;
  maxEstates: number;
  maxUsers: number;
  maxBlocks: number;
  aiGradingEnabled: boolean;
  aiCreditsMonthly: number;
  storageQuotaGb: number;
  priceMyrMonthly: string;
  isCustom: boolean;
  isActive: boolean;
}
interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

const STATUSES = [
  "TRIAL",
  "ACTIVE",
  "PAST_DUE",
  "SUSPENDED",
  "CANCELLED",
  "BLACKLISTED",
] as const;

const STATUS_TONE: Record<string, string> = {
  TRIAL: "bg-blue-100 text-blue-800",
  ACTIVE: "bg-emerald-100 text-emerald-800",
  PAST_DUE: "bg-amber-100 text-amber-800",
  SUSPENDED: "bg-orange-100 text-orange-800",
  CANCELLED: "bg-slate-100 text-slate-700",
  BLACKLISTED: "bg-red-100 text-red-800",
};

export function AdminHome() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = getAdminSession();
  const [tab, setTab] = useState<"tenants" | "packages" | "admins">("tenants");
  const [error, setError] = useState<string | null>(null);
  const [topupTenantId, setTopupTenantId] = useState<string | null>(null);
  const [topupAmount, setTopupAmount] = useState(100);

  const tenantsQ = useQuery({
    queryKey: ["pa-tenants"],
    queryFn: () =>
      adminFetch<{ data: TenantRow[] }>("GET", "/platform-admin/tenants"),
  });
  const packagesQ = useQuery({
    queryKey: ["pa-packages"],
    queryFn: () =>
      adminFetch<{ data: Pkg[] }>("GET", "/platform-admin/packages"),
  });
  const adminsQ = useQuery({
    queryKey: ["pa-admins"],
    queryFn: () =>
      adminFetch<{ data: AdminUser[] }>("GET", "/platform-admin/admins"),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pa-tenants"] });
    qc.invalidateQueries({ queryKey: ["pa-admins"] });
  };

  const run = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const patchTenant = useMutation({
    mutationFn: async (args: { id: string; patch: Record<string, unknown> }) => {
      setError(null);
      try {
        await adminFetch("PATCH", `/platform-admin/tenants/${args.id}`, args.patch);
        invalidate();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
  });

  const tabBtn = (t: typeof tab, label: string) => (
    <button
      onClick={() => setTab(t)}
      className={`rounded-t px-4 py-2 text-xs font-medium ${
        tab === t
          ? "border border-b-0 border-border bg-surface-2"
          : "text-text-secondary hover:text-text-primary"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-surface-1 p-4">
      <div className="mx-auto max-w-5xl">
        <header className="mb-4 flex items-center justify-between rounded-xl border border-border bg-surface-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <IconShieldLock size={20} className="text-text-accent" />
            <div className="text-[15px] font-medium">
              ASI Builder · Platform Admin
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-text-secondary">
            <span>
              {session?.user.name} · {session?.user.role}
            </span>
            <button
              onClick={() => {
                clearAdminSession();
                navigate("/admin/login", { replace: true });
              }}
              className="flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-surface-1"
            >
              <IconLogout size={13} /> Sign out
            </button>
          </div>
        </header>

        {error && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
            {error}
          </div>
        )}

        <div className="flex gap-1">
          {tabBtn("tenants", `Tenants (${tenantsQ.data?.data.length ?? "…"})`)}
          {tabBtn("packages", `Packages (${packagesQ.data?.data.length ?? "…"})`)}
          {tabBtn("admins", `Admins (${adminsQ.data?.data.length ?? "…"})`)}
        </div>

        <div className="rounded-b-xl rounded-tr-xl border border-border bg-surface-2">
          {/* ── Tenants ── */}
          {tab === "tenants" && (
            <table className="w-full text-xs">
              <thead className="border-b border-border bg-surface-1 text-text-secondary">
                <tr>
                  <th className="p-2 text-left">Tenant</th>
                  <th className="p-2 text-left">Type · Tier</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Package</th>
                  <th className="p-2 text-right" title="AI grading credit balance">
                    Credits
                  </th>
                  <th className="p-2 text-center">AI</th>
                  <th className="p-2 text-center">Top-up</th>
                </tr>
              </thead>
              <tbody>
                {(tenantsQ.data?.data ?? []).map(({ tenant: t, package: pkg }) => (
                  <tr key={t.id} className="border-b border-border align-top">
                    <td className="p-2">
                      <div className="font-medium">{t.name}</div>
                      <div className="font-mono text-[10px] text-text-secondary">
                        {t.code}
                      </div>
                    </td>
                    <td className="p-2 text-text-secondary">
                      {t.clientType} · {t.tier}
                    </td>
                    <td className="p-2">
                      <select
                        value={t.status}
                        onChange={(e) =>
                          patchTenant.mutate({
                            id: t.id,
                            patch: { status: e.target.value },
                          })
                        }
                        className={`rounded border-0 px-2 py-1 text-[10px] font-medium ${STATUS_TONE[t.status] ?? ""}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <select
                        value={t.subscriptionPackageId ?? ""}
                        onChange={(e) =>
                          patchTenant.mutate({
                            id: t.id,
                            patch: {
                              subscriptionPackageId: e.target.value || null,
                            },
                          })
                        }
                        className="rounded border border-border bg-surface-0 px-2 py-1 text-[10px]"
                      >
                        <option value="">— none —</option>
                        {(packagesQ.data?.data ?? []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      {pkg && (
                        <div className="mt-0.5 font-mono text-[10px] text-text-secondary">
                          {pkg.code}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      <div className="font-medium">{t.aiGradingCreditsBalance}</div>
                      <div className="text-[10px] text-text-secondary">
                        +{t.aiGradingCreditsMonthly}/mo
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={t.aiGradingEnabled}
                        disabled={t.aiForbidden}
                        title={
                          t.aiForbidden
                            ? "AI contractually forbidden for this tenant"
                            : "AI grading enabled"
                        }
                        onChange={(e) =>
                          patchTenant.mutate({
                            id: t.id,
                            patch: { aiGradingEnabled: e.target.checked },
                          })
                        }
                      />
                    </td>
                    <td className="p-2 text-center">
                      {topupTenantId === t.id ? (
                        <span className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            value={topupAmount}
                            onChange={(e) =>
                              setTopupAmount(Number(e.target.value) || 0)
                            }
                            className="w-20 rounded border border-border px-1.5 py-0.5"
                          />
                          <button
                            onClick={run(async () => {
                              await adminFetch(
                                "POST",
                                `/platform-admin/tenants/${t.id}/credits/topup`,
                                {
                                  amount: topupAmount,
                                  summary: `Manual top-up by ${session?.user.email}`,
                                },
                              );
                              setTopupTenantId(null);
                            })}
                            className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white"
                          >
                            OK
                          </button>
                          <button
                            onClick={() => setTopupTenantId(null)}
                            className="rounded border border-border px-1.5 py-0.5 text-[10px]"
                          >
                            ✕
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setTopupTenantId(t.id)}
                          className="rounded border border-border px-2 py-0.5 text-[10px] hover:bg-surface-1"
                        >
                          + credits
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ── Packages ── */}
          {tab === "packages" && (
            <table className="w-full text-xs">
              <thead className="border-b border-border bg-surface-1 text-text-secondary">
                <tr>
                  <th className="p-2 text-left">Package</th>
                  <th className="p-2 text-right">Companies</th>
                  <th className="p-2 text-right">Estates</th>
                  <th className="p-2 text-right">Users</th>
                  <th className="p-2 text-right">Blocks</th>
                  <th className="p-2 text-right">Storage</th>
                  <th className="p-2 text-center">AI</th>
                  <th className="p-2 text-right">AI credits/mo</th>
                  <th className="p-2 text-right">RM/month</th>
                </tr>
              </thead>
              <tbody>
                {(packagesQ.data?.data ?? []).map((p) => (
                  <tr key={p.id} className="border-b border-border">
                    <td className="p-2">
                      <div className="font-medium">{p.name}</div>
                      <div className="font-mono text-[10px] text-text-secondary">
                        {p.code}
                        {p.isCustom ? " · custom" : ""}
                      </div>
                    </td>
                    <td className="p-2 text-right tabular-nums">{p.maxCompanies}</td>
                    <td className="p-2 text-right tabular-nums">{p.maxEstates}</td>
                    <td className="p-2 text-right tabular-nums">{p.maxUsers}</td>
                    <td className="p-2 text-right tabular-nums">{p.maxBlocks}</td>
                    <td className="p-2 text-right tabular-nums">{p.storageQuotaGb} GB</td>
                    <td className="p-2 text-center">{p.aiGradingEnabled ? "✓" : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{p.aiCreditsMonthly}</td>
                    <td className="p-2 text-right font-medium tabular-nums">
                      {Number(p.priceMyrMonthly).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ── Admins ── */}
          {tab === "admins" && (
            <table className="w-full text-xs">
              <thead className="border-b border-border bg-surface-1 text-text-secondary">
                <tr>
                  <th className="p-2 text-left">Admin</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Since</th>
                  <th className="p-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {(adminsQ.data?.data ?? []).map((a) => (
                  <tr key={a.id} className="border-b border-border">
                    <td className="p-2">
                      <div className="font-medium">{a.name}</div>
                      <div className="text-[10px] text-text-secondary">{a.email}</div>
                    </td>
                    <td className="p-2">{a.role}</td>
                    <td className="p-2">
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] ${a.isActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                      >
                        {a.isActive ? "ACTIVE" : "PENDING"}
                      </span>
                    </td>
                    <td className="p-2 text-text-secondary">
                      {a.createdAt?.slice(0, 10)}
                    </td>
                    <td className="p-2 text-center">
                      {a.isActive ? (
                        <button
                          onClick={run(() =>
                            adminFetch(
                              "POST",
                              `/platform-admin/admins/${a.id}/deactivate`,
                              {},
                            ),
                          )}
                          className="rounded border border-border px-2 py-0.5 text-[10px] hover:bg-surface-1"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={run(() =>
                            adminFetch(
                              "POST",
                              `/platform-admin/admins/${a.id}/activate`,
                              {},
                            ),
                          )}
                          className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white"
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
